import { Component, Inject, Input } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
  concat,
  filter,
  findIndex,
  forEach,
  meanBy,
  orderBy,
  reject,
} from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { TINYMCE_EDITOR_OPTIONS, TinymceEditorOptions } from '../providers';
import { BlockTypes, Blocks, blockGuards } from './../interfaces';

@Component({
    selector: 'la-document-builder-content',
    templateUrl: './document-builder-content.component.html',
    styleUrls: ['./document-builder-content.component.scss'],
    standalone: false
})
export class DocumentBuilderContentComponent {
  // Component logic goes here
  form = new FormGroup({});

  @Input() get blocks() {
    return this.blocksChanges$.value;
  }
  set blocks(value: Blocks[]) {
    // this.documentManager.init(value);
    this.setInnerForm(value);
    this.blocksChanges$.next(value);
  }
  blocksChanges$ = new BehaviorSubject<Blocks[]>([]);

  blockTypes = BlockTypes;
  blockGuards = blockGuards;

  constructor(
    @Inject(TINYMCE_EDITOR_OPTIONS) public editorOptions: TinymceEditorOptions
  ) {}

  trackBy(index: number, x: Blocks) {
    return x.id;
  }

  addItemBefore(block: Blocks) {
    const id = uuidv4();
    const value = 'Enter text here';

    const newBlock = {
      id,
      type: BlockTypes.EDITOR,
      initialValue: value,
      required: false,
      hidden: false,
      order: this.calcOrderBefore(block),
    };

    const newBlocks = [...this.blocks, newBlock];
    this.blocksChanges$.next(newBlocks);
    this.form.addControl(id, new FormControl(value));
  }

  addItemAfter(block: Blocks) {
    const id = uuidv4();
    const value = 'Enter text here';

    const newBlock = {
      id,
      type: BlockTypes.EDITOR,
      initialValue: value,
      required: false,
      hidden: false,
      order: this.calcOrderAfter(block),
    };

    const newBlocks = [...this.blocks, newBlock];
    this.blocksChanges$.next(newBlocks);
    this.form.addControl(id, new FormControl(value));
  }

  remove(block: Blocks) {
    const newBlocks = reject(this.blocks, { id: block.id });

    this.blocksChanges$.next(newBlocks);
    if (blockGuards.isEditor(block)) {
      this.form.removeControl(block.id);
    }
  }

  toggleVisibilityAt(block: Blocks) {
    const newBlock = {
      ...block,
      hidden: !block.hidden,
    };
    const newBlocks = concat(reject(this.blocks, { id: block.id }), newBlock);
    this.blocksChanges$.next(newBlocks);
  }

  insertPageBreak(block: Blocks) {
    const id = uuidv4();
    const newBlock = {
      id,
      type: BlockTypes.PAGE_BREAK,
      required: false,
      hidden: false,
      order: this.calcOrderAfter(block),
    };
    const newBlocks = [...this.blocks, newBlock];
    this.blocksChanges$.next(newBlocks);
  }

  private calcOrderAfter(block: Blocks) {
    const orderedBlocks = orderBy(this.blocks, 'order');
    const index = findIndex(orderedBlocks, { id: block.id });

    let newOrder;

    if (index === orderedBlocks.length - 1) {
      newOrder = orderedBlocks[index].order + 1;
    } else {
      const betweenBlocks = [orderedBlocks[index], orderedBlocks[index + 1]];
      newOrder = meanBy(betweenBlocks, 'order');
    }

    return newOrder;
  }

  private calcOrderBefore(block: Blocks) {
    const orderedBlocks = orderBy(this.blocks, 'order');
    const index = findIndex(orderedBlocks, { id: block.id });

    let newOrder;

    if (index === 0) {
      newOrder = meanBy([0, orderedBlocks[0]], 'order');
    } else {
      const betweenBlocks = [orderedBlocks[index - 1], orderedBlocks[index]];
      newOrder = meanBy(betweenBlocks, 'order');
    }

    return newOrder;
  }

  private setInnerForm(blocks: Blocks[]): void {
    const editorBlocks = filter(blocks, { type: 'editor' });
    forEach(editorBlocks, (block) => {
      if (!blockGuards.isEditor(block) || this.form.contains(block.id)) {
        return;
      }
      this.form.addControl(block.id, new FormControl(block.initialValue));
    });
  }
}
